import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  CircularProgress,
} from "@mui/material";
import { noWheelInputProps } from "util/utils";

// Nash API Requirements based on https://docs.usenash.com/api-reference/order/order-requirements
const NASH_REQUIREMENTS = [
  { value: "photo_proof_of_delivery", label: "Photo Proof of Delivery" },
  { value: "signature_proof_of_delivery", label: "Signature Proof of Delivery" },
  { value: "photo_proof_of_pickup", label: "Photo Proof of Pickup" },
  { value: "signature_proof_of_pickup", label: "Signature Proof of Pickup" },
  { value: "age_verification_on_delivery", label: "Age Verification on Delivery" },
  { value: "alcohol", label: "Alcohol (ID Verification)" },
  { value: "tobacco", label: "Tobacco (ID Verification)" },
  { value: "id_verification_on_delivery", label: "ID Verification on Delivery" },
  { value: "barcode_scan_on_pickup", label: "Barcode Scan on Pickup" },
  { value: "barcode_scan_on_dropoff", label: "Barcode Scan on Dropoff" },
  { value: "meet_on_delivery", label: "Meet on Delivery" },
  { value: "shipping_label", label: "Shipping Label" },
  { value: "two_person_team", label: "Two Person Team" },
  { value: "pincode_verification", label: "Pincode Verification" },
  { value: "parking_check_in", label: "Parking Check-In" },
  { value: "schedule_ii_controlled_substances", label: "Schedule II Controlled Substances" },
  { value: "pick_and_pack", label: "Pick and Pack" },
];

const DeliverySettingsDialog = ({ open, onClose, onSubmit, initialValues, loading }) => {
  const [formValues, setFormValues] = useState({
    deliveryServicePhone: "",
    deliveryRequirements: [],
    deliveryServiceMealPrepareTime: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialValues) {
      setFormValues({
        deliveryServicePhone: initialValues.deliveryServicePhone || "",
        deliveryRequirements: initialValues.deliveryRequirements || [],
        deliveryServiceMealPrepareTime: initialValues.deliveryServiceMealPrepareTime || "",
      });
    }
  }, [initialValues]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleRequirementsChange = (event) => {
    const { value } = event.target;
    setFormValues(prev => ({
      ...prev,
      deliveryRequirements: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formValues.deliveryServicePhone.trim()) {
      newErrors.deliveryServicePhone = "Delivery service phone is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    
    if (validateForm()) {
      onSubmit(formValues);
      // Don't close here - parent will close after successful save
    }
  };

  const handleCancel = () => {
    setFormValues({
      deliveryServicePhone: initialValues?.deliveryServicePhone || "",
      deliveryRequirements: initialValues?.deliveryRequirements || [],
      deliveryServiceMealPrepareTime: initialValues?.deliveryServiceMealPrepareTime || "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delivery Settings</DialogTitle>
      <DialogContent>
        <form onSubmit={handleFormSubmit}>
          <Grid container spacing={3} style={{ marginTop: '8px' }}>
            <Grid item xs={12}>
              <TextField
                label="Delivery Service Phone"
                name="deliveryServicePhone"
                value={formValues.deliveryServicePhone}
                onChange={handleFormChange}
                fullWidth
                required
                error={!!errors.deliveryServicePhone}
                helperText={errors.deliveryServicePhone || "Phone number for delivery service inquiries"}
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel id="delivery-requirements-label">Order Requirements</InputLabel>
                <Select
                  labelId="delivery-requirements-label"
                  id="deliveryRequirements"
                  multiple
                  value={formValues.deliveryRequirements}
                  onChange={handleRequirementsChange}
                  input={<OutlinedInput label="Order Requirements" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const requirement = NASH_REQUIREMENTS.find(r => r.value === value);
                        return (
                          <Chip 
                            key={value} 
                            label={requirement?.label || value} 
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {NASH_REQUIREMENTS.map((requirement) => (
                    <MenuItem key={requirement.value} value={requirement.value}>
                      {requirement.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Meal Prepare Time (minutes)"
                name="deliveryServiceMealPrepareTime"
                value={formValues.deliveryServiceMealPrepareTime}
                onChange={handleFormChange}
                fullWidth
                type="number"
                error={!!errors.deliveryServiceMealPrepareTime}
                helperText={errors.deliveryServiceMealPrepareTime || "Time needed to prepare meals for delivery service"}
                variant="outlined"
                margin="dense"
                inputProps={{ ...noWheelInputProps, min: 0 }}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleFormSubmit} 
          color="primary" 
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeliverySettingsDialog;